import {
  createAdminAchievement,
  deleteAdminAchievement,
  fetchAdminAchievements,
  updateAdminAchievement,
  type AchievementCriteriaPayload,
  type AdminAchievementRow,
} from "./achievements-api";

const SCORE_MODES: Array<{ id: string; label: string }> = [
  { id: "normal", label: "Normal" },
  { id: "wasd", label: "WASD" },
  { id: "superdot", label: "Superdot" },
  { id: "roulette", label: "Roulette" },
  { id: "sandbox", label: "Sandbox" },
  { id: "highway", label: "Highway" },
  { id: "darkness", label: "Darkness" },
  { id: "mirror", label: "Mirror" },
  { id: "snake", label: "Snake" },
  { id: "combo", label: "Combo" },
  { id: "overload", label: "Overload" },
  { id: "colorrush", label: "Color Rush" },
  { id: "heat", label: "Heat" },
  { id: "hunter", label: "Hunter" },
  { id: "pop", label: "POP" },
  { id: "counter", label: "Counter" },
  { id: "aim", label: "AIM" },
  { id: "hook", label: "Hook" },
  { id: "tilt", label: "Tilt" },
  { id: "orbit", label: "Orbit" },
  { id: "progression", label: "Progression" },
  { id: "speedrun", label: "SpeedRun" },
];

type GoalKind = "modeScore" | "dotsBalance" | "friends" | "playtime" | "deaths";

function parseSpeedrunTimeInput(raw: string): number | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  const minM = /(\d+)\s*m/.exec(s);
  const secM = /(\d+(?:\.\d+)?)\s*s/.exec(s);
  if (!minM && !secM) {
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? Math.floor(n * 1000) : null;
  }
  const m = minM ? Number(minM[1]) : 0;
  const sec = secM ? Number(secM[1]) : 0;
  if (!Number.isFinite(m) || !Number.isFinite(sec) || m < 0 || sec < 0) return null;
  const ms = Math.floor(m * 60000 + sec * 1000);
  return ms > 0 ? ms : null;
}

function formatSpeedrunTimeInput(ms: number): string {
  const m = Math.floor(ms / 60000);
  const s = (ms % 60000) / 1000;
  if (m <= 0) return `${s.toFixed(2)}s`;
  return `${m}m ${s < 10 ? "0" : ""}${s.toFixed(2)}s`;
}

function parsePlaytimeSecInput(raw: string): number | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  const dayM = /(\d+)\s*d/.exec(s);
  const hourM = /(\d+)\s*h/.exec(s);
  const minM = /(\d+)\s*m/.exec(s);
  const secM = /(\d+)\s*s/.exec(s);
  if (!dayM && !hourM && !minM && !secM) {
    const n = Math.floor(Number(s));
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const d = dayM ? Number(dayM[1]) : 0;
  const h = hourM ? Number(hourM[1]) : 0;
  const m = minM ? Number(minM[1]) : 0;
  const r = secM ? Number(secM[1]) : 0;
  const total = d * 86400 + h * 3600 + m * 60 + r;
  return total > 0 ? total : null;
}

function positiveInt(raw: string): number | null {
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) && n >= 1 ? n : null;
}

export function mountAdminAchievements(root: HTMLElement) {
  root.replaceChildren();

  const toolbar = document.createElement("div");
  toolbar.className = "modeBox adminRecordsFilters";
  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "btn green";
  addBtn.textContent = "Add Achievement";
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn";
  refreshBtn.textContent = "Refresh";
  toolbar.appendChild(addBtn);
  toolbar.appendChild(refreshBtn);
  root.appendChild(toolbar);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  const editor = document.createElement("div");
  editor.className = "modeBox adminAchievementEditor hidden";
  root.appendChild(editor);

  const listWrap = document.createElement("div");
  listWrap.className = "leaderboardWrap";
  root.appendChild(listWrap);

  let editingId: string | null = null;

  const titleInput = document.createElement("input");
  titleInput.className = "formInput";
  titleInput.placeholder = "Achievement title";
  const descInput = document.createElement("textarea");
  descInput.className = "formInput";
  descInput.rows = 3;
  descInput.placeholder = "Description shown to players (optional)";
  const kindSelect = document.createElement("select");
  kindSelect.className = "formInput";
  (
    [
      ["modeScore", "Mode score"],
      ["dotsBalance", "Dots"],
      ["friends", "Friends"],
      ["playtime", "Playtime"],
      ["deaths", "Deaths"],
    ] as const
  ).forEach(([v, label]) => {
    const o = document.createElement("option");
    o.value = v;
    o.textContent = label;
    kindSelect.appendChild(o);
  });

  const criteriaBox = document.createElement("div");
  criteriaBox.className = "adminAchievementCriteriaBox";

  const modeSelect = document.createElement("select");
  modeSelect.className = "formInput";
  modeSelect.dataset.field = "mode";
  SCORE_MODES.forEach((m) => {
    const o = document.createElement("option");
    o.value = m.id;
    o.textContent = m.label;
    modeSelect.appendChild(o);
  });

  const showHowInput = document.createElement("input");
  showHowInput.type = "checkbox";
  showHowInput.className = "adminAchievementShowHowCheck";
  showHowInput.id = "adminAchievementShowHowCheck";

  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "btn green";
  saveBtn.textContent = "Save";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "btn";
  cancelBtn.textContent = "Cancel";
  const editorErr = document.createElement("p");
  editorErr.className = "accessHint";

  const criteriaRow = (label: string, input: HTMLElement) => {
    const fr = document.createElement("div");
    fr.className = "formRow";
    const sp = document.createElement("span");
    sp.className = "formLabel";
    sp.textContent = label;
    fr.appendChild(sp);
    fr.appendChild(input);
    criteriaBox.appendChild(fr);
  };

  const criteriaInput = (field: string, type: "number" | "text", placeholder = "") => {
    const el = document.createElement("input");
    el.type = type;
    if (type === "number") el.min = "1";
    el.className = "formInput";
    el.dataset.field = field;
    if (placeholder) el.placeholder = placeholder;
    return el;
  };

  function appendModeSubFields() {
    const mode = modeSelect.value;
    if (mode === "progression") {
      criteriaRow("Reach level", criteriaInput("minLevel", "number"));
    } else if (mode === "speedrun") {
      criteriaRow("SpeedRun level", criteriaInput("level", "number"));
      criteriaRow("Max time", criteriaInput("maxTime", "text", "e.g. 2m 30s or 90"));
    } else {
      criteriaRow("Reach score", criteriaInput("minScore", "number", "Target score"));
    }
  }

  function rebuildModeSubFields() {
    while (criteriaBox.children.length > 1) {
      criteriaBox.lastElementChild?.remove();
    }
    appendModeSubFields();
  }

  function buildCriteriaFields(preserveMode?: string) {
    criteriaBox.replaceChildren();
    const kind = kindSelect.value as GoalKind;

    if (kind === "modeScore") {
      if (preserveMode) modeSelect.value = preserveMode;
      criteriaRow("Mode", modeSelect);
      appendModeSubFields();
      return;
    }

    if (kind === "friends" || kind === "deaths") {
      const n = criteriaInput("minCount", "number");
      criteriaRow(kind === "friends" ? "Friends needed" : "Total deaths", n);
      return;
    }

    if (kind === "dotsBalance") {
      const n = criteriaInput("minDots", "number", "Target Dots");
      criteriaRow("Dots", n);
      return;
    }

    if (kind === "playtime") {
      const pt = criteriaInput("minSec", "text", "e.g. 1h 30m or 3600");
      criteriaRow("Total playtime", pt);
    }
  }

  modeSelect.onchange = () => {
    if (kindSelect.value === "modeScore") rebuildModeSubFields();
  };
  kindSelect.onchange = () => buildCriteriaFields();

  const editorTitle = document.createElement("h3");
  editorTitle.textContent = "Achievement editor";
  editor.appendChild(editorTitle);
  const tRow = document.createElement("div");
  tRow.className = "formRow";
  tRow.innerHTML = `<span class="formLabel">Title</span>`;
  tRow.appendChild(titleInput);
  editor.appendChild(tRow);
  const dRow = document.createElement("div");
  dRow.className = "formRow";
  dRow.innerHTML = `<span class="formLabel">Description</span>`;
  dRow.appendChild(descInput);
  editor.appendChild(dRow);
  const showHowRow = document.createElement("label");
  showHowRow.className = "formRow adminAchievementShowHowRow";
  showHowRow.htmlFor = showHowInput.id;
  const showHowText = document.createElement("span");
  showHowText.className = "formLabel";
  showHowText.textContent = "Show how to complete";
  showHowRow.appendChild(showHowInput);
  showHowRow.appendChild(showHowText);
  editor.appendChild(showHowRow);

  const dotsRewardInput = document.createElement("input");
  dotsRewardInput.type = "number";
  dotsRewardInput.min = "0";
  dotsRewardInput.className = "formInput";
  dotsRewardInput.placeholder = "0";
  const dotsRow = document.createElement("div");
  dotsRow.className = "formRow";
  dotsRow.innerHTML = `<span class="formLabel">Dots reward</span>`;
  dotsRow.appendChild(dotsRewardInput);
  editor.appendChild(dotsRow);

  const kRow = document.createElement("div");
  kRow.className = "formRow";
  kRow.innerHTML = `<span class="formLabel">Goal type</span>`;
  kRow.appendChild(kindSelect);
  editor.appendChild(kRow);
  editor.appendChild(criteriaBox);
  editor.appendChild(editorErr);
  const btnRow = document.createElement("div");
  btnRow.className = "rgbRow";
  btnRow.style.justifyContent = "flex-end";
  btnRow.style.gap = "8px";
  btnRow.appendChild(cancelBtn);
  btnRow.appendChild(saveBtn);
  editor.appendChild(btnRow);
  buildCriteriaFields();

  const fieldVal = (f: string) => {
    const el = criteriaBox.querySelector(`[data-field="${f}"]`) as HTMLInputElement | HTMLSelectElement | null;
    return el ? String(el.value ?? "").trim() : "";
  };

  function readCriteriaFromForm(): { criteria: AchievementCriteriaPayload | null; error: string } {
    const kind = kindSelect.value as GoalKind;

    if (kind === "modeScore") {
      const mode = fieldVal("mode") || modeSelect.value;
      if (mode === "progression") {
        const minLevel = positiveInt(fieldVal("minLevel"));
        if (minLevel == null) return { criteria: null, error: "Enter a target level (1 or higher)." };
        return { criteria: { kind: "progressionLevel", minLevel }, error: "" };
      }
      if (mode === "speedrun") {
        const level = positiveInt(fieldVal("level"));
        const maxTimeMs = parseSpeedrunTimeInput(fieldVal("maxTime"));
        if (level == null) return { criteria: null, error: "Enter a SpeedRun level (1 or higher)." };
        if (maxTimeMs == null) return { criteria: null, error: "Enter max time (e.g. 2m 30s or 90 for seconds)." };
        return { criteria: { kind: "speedrunTime", level, maxTimeMs }, error: "" };
      }
      const minScore = positiveInt(fieldVal("minScore"));
      if (!mode) return { criteria: null, error: "Select a mode." };
      if (minScore == null) return { criteria: null, error: "Enter a target score (1 or higher)." };
      return { criteria: { kind: "modeScore", mode, minScore }, error: "" };
    }

    if (kind === "friends" || kind === "deaths") {
      const minCount = positiveInt(fieldVal("minCount"));
      if (minCount == null) {
        return {
          criteria: null,
          error: kind === "friends" ? "Enter how many friends are needed." : "Enter the death count target.",
        };
      }
      return {
        criteria: kind === "friends" ? { kind: "friends", minCount } : { kind: "deaths", minCount },
        error: "",
      };
    }

    if (kind === "playtime") {
      const minSec = parsePlaytimeSecInput(fieldVal("minSec"));
      if (minSec == null) return { criteria: null, error: "Enter total playtime (e.g. 1h 30m or 3600)." };
      return { criteria: { kind: "playtime", minSec }, error: "" };
    }

    if (kind === "dotsBalance") {
      const minDots = positiveInt(fieldVal("minDots"));
      if (minDots == null) return { criteria: null, error: "Enter a Dots target (1 or higher)." };
      return { criteria: { kind: "dotsBalance", minDots }, error: "" };
    }

    return { criteria: null, error: "Select a goal type." };
  }

  function fillCriteriaForm(c: AchievementCriteriaPayload | null) {
    if (!c) {
      kindSelect.value = "modeScore";
      modeSelect.value = "normal";
      buildCriteriaFields("normal");
      return;
    }

    if (c.kind === "modeScore") {
      kindSelect.value = "modeScore";
      buildCriteriaFields(c.mode);
      const score = criteriaBox.querySelector('[data-field="minScore"]') as HTMLInputElement | null;
      if (score) score.value = String(c.minScore);
      return;
    }

    if (c.kind === "progressionLevel") {
      kindSelect.value = "modeScore";
      buildCriteriaFields("progression");
      const lvl = criteriaBox.querySelector('[data-field="minLevel"]') as HTMLInputElement | null;
      if (lvl) lvl.value = String(c.minLevel);
      return;
    }

    if (c.kind === "speedrunTime") {
      kindSelect.value = "modeScore";
      buildCriteriaFields("speedrun");
      const lvl = criteriaBox.querySelector('[data-field="level"]') as HTMLInputElement | null;
      const time = criteriaBox.querySelector('[data-field="maxTime"]') as HTMLInputElement | null;
      if (lvl) lvl.value = String(c.level);
      if (time) time.value = formatSpeedrunTimeInput(c.maxTimeMs);
      return;
    }

    kindSelect.value = c.kind;
    buildCriteriaFields();
    if (c.kind === "friends" || c.kind === "deaths") {
      const n = criteriaBox.querySelector('[data-field="minCount"]') as HTMLInputElement | null;
      if (n) n.value = String(c.minCount);
    } else if (c.kind === "playtime") {
      const pt = criteriaBox.querySelector('[data-field="minSec"]') as HTMLInputElement | null;
      if (pt) pt.value = String(c.minSec);
    } else if (c.kind === "dotsBalance") {
      const d = criteriaBox.querySelector('[data-field="minDots"]') as HTMLInputElement | null;
      if (d) d.value = String(c.minDots);
    }
  }

  function openEditor(row?: AdminAchievementRow) {
    editingId = row?.id ?? null;
    titleInput.value = row?.title ?? "";
    descInput.value = row?.description ?? "";
    showHowInput.checked = !!row?.showHowToComplete;
    dotsRewardInput.value = String(row?.dotsReward ?? 0);
    fillCriteriaForm(row?.criteria ?? null);
    editorErr.textContent = "";
    editor.classList.remove("hidden");
    saveBtn.textContent = editingId ? "Save changes" : "Save";
    editorTitle.textContent = editingId ? "Edit achievement" : "Add achievement";
  }

  function closeEditor() {
    editingId = null;
    editor.classList.add("hidden");
    editorErr.textContent = "";
  }

  addBtn.onclick = () => {
    showHowInput.checked = false;
    dotsRewardInput.value = "0";
    openEditor();
  };
  cancelBtn.onclick = () => closeEditor();
  refreshBtn.onclick = () => void load();

  saveBtn.onclick = () => {
    const title = titleInput.value.trim();
    const description = descInput.value.trim();
    const { criteria, error } = readCriteriaFromForm();
    if (!title) {
      editorErr.textContent = "Enter a title.";
      return;
    }
    if (!criteria) {
      editorErr.textContent = error || "Complete all goal fields.";
      return;
    }
    editorErr.textContent = "";
    const showHowToComplete = showHowInput.checked;
    const dotsReward = Math.max(0, Math.floor(Number(dotsRewardInput.value) || 0));
    void (async () => {
      try {
        if (editingId) {
          await updateAdminAchievement({ id: editingId, title, description, criteria, showHowToComplete, dotsReward });
        } else {
          await createAdminAchievement({ title, description, criteria, showHowToComplete, dotsReward });
        }
        closeEditor();
        await load();
      } catch {
        editorErr.textContent = "Save failed (forbidden or network).";
      }
    })();
  };

  async function load() {
    listWrap.innerHTML = "";
    status.textContent = "Loading…";
    try {
      const rows = await fetchAdminAchievements();
      if (!rows.length) {
        status.textContent = "No achievements yet. Press Add Achievement.";
        return;
      }
      status.textContent = `${rows.length} achievement(s).`;
      const tbl = document.createElement("table");
      tbl.className = "lbTable adminRecordsTable";
      const thead = document.createElement("thead");
      const hr = document.createElement("tr");
      ["Title", "Goal", "Dots", "Actions"].forEach((h) => {
        const th = document.createElement("th");
        th.textContent = h;
        hr.appendChild(th);
      });
      thead.appendChild(hr);
      tbl.appendChild(thead);
      const tb = document.createElement("tbody");
      for (const row of rows) {
        const tr = document.createElement("tr");
        const tdT = document.createElement("td");
        const descHtml = row.description
          ? `<br><span style="opacity:.85;font-size:9px">${row.description}</span>`
          : "";
        tdT.innerHTML = `<strong>${row.title}</strong>${descHtml}`;
        const tdG = document.createElement("td");
        tdG.textContent = row.criteriaSummary;
        const tdDots = document.createElement("td");
        tdDots.textContent = String(row.dotsReward ?? 0);
        const tdA = document.createElement("td");
        tdA.className = "adminRecordsActions";
        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.className = "btn adminRecordsBtn";
        editBtn.textContent = "Edit";
        editBtn.onclick = () => openEditor(row);
        const delBtn = document.createElement("button");
        delBtn.type = "button";
        delBtn.className = "btn adminRecordsBtn";
        delBtn.textContent = "Delete";
        delBtn.onclick = () => {
          if (!confirm(`Delete achievement "${row.title}"?`)) return;
          void (async () => {
            try {
              await deleteAdminAchievement(row.id);
              await load();
            } catch {
              status.textContent = "Delete failed.";
            }
          })();
        };
        tdA.appendChild(editBtn);
        tdA.appendChild(delBtn);
        tr.appendChild(tdT);
        tr.appendChild(tdG);
        tr.appendChild(tdDots);
        tr.appendChild(tdA);
        tb.appendChild(tr);
      }
      tbl.appendChild(tb);
      listWrap.appendChild(tbl);
    } catch {
      status.textContent = "Could not load achievements.";
    }
  }

  void load();

  return { refresh: () => void load() };
}
